import { createFileRoute, Link } from '@tanstack/react-router'
import { SectionContainer } from '@repo/ui/section'
import { Button } from '@repo/ui/button'
import { CheckCircleIcon, ArrowRightIcon } from '@phosphor-icons/react'
import { YOUTUBE_URL } from '@/shared/lib/constants'
import { PremiumHeading, PremiumCard } from '@/components/shared'
import { seo } from '@/shared/lib/seo'

// noindex: a post-signup confirmation has no search intent behind it, and an
// indexed thank-you page both leaks into results for brand queries and inflates
// conversion counts when people arrive on it directly from Google.
export const Route = createFileRoute('/thank-you')({
  head: () => ({
    ...seo({
      title: "You're subscribed | ControlStack.ai",
      description:
        'Thanks for subscribing to the ControlStack build notes. Check your email for the first message.',
      path: '/thank-you',
      noindex: true,
    }),
  }),
  component: ThankYouPage,
})

function ThankYouPage() {
  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-20 bg-[#0a0f0d]">
      <SectionContainer>
        <div className="max-w-2xl mx-auto text-center">
          {/* Success Icon */}
          <div className="inline-flex items-center justify-center size-20 rounded-full bg-[#80cca5]/20 text-[#80cca5] mb-6">
            <CheckCircleIcon className="size-10" weight="fill" />
          </div>

          {/* Main Message */}
          <PremiumHeading as="h1" className="text-4xl md:text-5xl mb-4">
            You're In! 🎉
          </PremiumHeading>
          <p className="text-xl text-slate-300 mb-8">
            Check your email in the next 5 minutes for your first message from
            me.
          </p>

          {/* What's Next */}
          <PremiumCard className="text-left mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">
              What happens next?
            </h2>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="text-[#80cca5] font-bold text-lg">1.</span>
                <div>
                  <p className="text-slate-300">
                    I'll read your biggest challenge personally (I read every
                    single response)
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#80cca5] font-bold text-lg">2.</span>
                <div>
                  <p className="text-slate-300">
                    You'll get practical build notes, templates, and guide
                    updates as they are published
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#80cca5] font-bold text-lg">3.</span>
                <div>
                  <p className="text-slate-300">
                    I'll share new guides on OpenCode, agents, code review, and
                    shipping real projects
                  </p>
                </div>
              </li>
            </ul>
          </PremiumCard>

          {/* While You Wait */}
          <div className="border-t border-slate-700 pt-8 mb-8">
            <h3 className="text-xl font-semibold text-white mb-4">
              While you wait, watch the latest builds
            </h3>
            <p className="text-slate-300 mb-6">
              See practical AI development workflows, guides, and project builds
              on YouTube. No hype, just useful systems.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={YOUTUBE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg bg-[#80cca5] hover:bg-[#6bb890] text-[#0e1110] font-semibold transition-colors"
              >
                Watch the Builds
                <ArrowRightIcon className="size-5" />
              </a>
            </div>
          </div>

          {/* Back Home */}
          <Link to="/">
            <Button variant="ghost" className="text-slate-400">
              <ArrowRightIcon className="size-4 rotate-180" />
              Back to home
            </Button>
          </Link>
        </div>
      </SectionContainer>
    </div>
  )
}
